import { Injectable } from '@angular/core';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { SupabaseService } from './supabase.service';
import { environment } from '../../environments/environment';

export interface JobPaymentData {
  title: string;
  company: string;
  premium_support: boolean;
  sticky_duration: string;
  has_logo: boolean;
  highlight_type: string;
  total_amount: number;
  job_data: any; // Complete job post data
}

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  private stripePromise: Promise<Stripe | null>;

  constructor(private supabaseService: SupabaseService) {
    this.stripePromise = loadStripe(environment.stripePublishableKey);
  }

  async createCheckoutSession(paymentData: JobPaymentData): Promise<void> {
    try {
      const stripe = await this.stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Call Supabase edge function to create checkout session
      const { data, error } = await this.supabaseService
        .getClient()
        .functions
        .invoke('create-stripe-checkout', {
          body: {
            job_data: paymentData.job_data,
            total_amount: paymentData.total_amount,
            success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}/post-job?cancelled=true`
          }
        });

      if (error) {
        console.error('Error creating checkout session:', error);
        throw new Error('Failed to create checkout session');
      }

      if (!data?.session_id) {
        throw new Error('No session ID returned from checkout creation');
      }

      // Redirect to Stripe checkout
      const result = await stripe.redirectToCheckout({
        sessionId: data.session_id
      });

      if (result.error) {
        console.error('Stripe checkout error:', result.error);
        throw new Error(result.error.message || 'Checkout failed');
      }

    } catch (error) {
      console.error('Error in createCheckoutSession:', error);
      throw error;
    }
  }

  async retrieveCheckoutSession(sessionId: string): Promise<any> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .functions
        .invoke('get-checkout-session', {
          body: { session_id: sessionId }
        });

      if (error) {
        console.error('Error retrieving checkout session:', error);
        throw new Error('Failed to retrieve checkout session');
      }

      return data;
    } catch (error) {
      console.error('Error in retrieveCheckoutSession:', error);
      throw error;
    }
  }
} 